import axios from "axios";
import { useEffect, useState } from "react";
import { Calculator, Info } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useGstClasses } from "./GstClass";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const planCache = {};
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function useTakeRate() {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!user?.id || user.role === "buyer" || user.role === "admin") return;
    planCache[user.id] = planCache[user.id] || axios.get(`${API}/users/${user.id}/subscription`).then(r => r.data?.plan || null);
    planCache[user.id].then(setPlan).catch(() => { delete planCache[user.id]; });
  }, [user?.id, user?.role]);
  const pct = plan ? (plan.design_take_rate_pct ?? plan.platform_take_rate_pct) : null;
  return { pct, planName: plan?.name, role: user?.role };
}

export function GrossBidNotice({ amount, label = "bid", gstRate, gstClass }) {
  const { pct, planName, role } = useTakeRate();
  const classes = useGstClasses();
  const party = ["material_supplier", "finishing_partner"].includes(role) && label === "quote" ? "printer" : "buyer";
  if (pct == null) return null;
  const rate = gstRate ?? classes.find(c => c.id === gstClass)?.rate_pct ?? (role === "agency" ? 18 : null);
  const gross = Number(amount) || 0;
  const fee = Math.round(gross * pct) / 100;
  const feeGst = Math.round(fee * 18) / 100;
  const gst = rate != null ? Math.round(gross * rate) / 100 : null;
  const tds = Math.round(gross) / 100;
  return (
    <div data-testid="gross-bid-notice" style={{ gridColumn: "1 / -1", display: "flex", gap: 10, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 12px", fontSize: 12, lineHeight: 1.5, color: "#78350F", textTransform: "none", letterSpacing: 0, fontFamily: "inherit" }}>
      <Info size={15} style={{ flexShrink: 0, marginTop: 2 }} />
      <div>
        <b>Submit a Gross {label}: your final pre-GST price, including our {pct}% platform fee ({planName} plan).</b> GoPrintPack is the Merchant of Record: the {party} sees only your {label} price (plus GST{label === "bid" ? " and delivery" : ""}). Your identity and the platform fee are never shown to them.
        {gross > 0 && (
          <div data-testid="gross-bid-net" style={{ marginTop: 4, fontWeight: 700, color: "#065F46" }}>
            You invoice us {inr(gross)}{gst != null && <> + {inr(gst)} GST @{rate}%</>}. At payout we deduct the platform fee {inr(fee)} + {inr(feeGst)} GST @18% (separate tax invoice, so you can claim it as ITC) and {inr(tds)} TDS 1% u/s 194-O (shown in your Form 26AS).
            {gst != null && <> Net payout: <span data-testid="gross-bid-payout">{inr(gross + gst - fee - feeGst - tds)}</span>.</>}
          </div>
        )}
      </div>
    </div>
  );
}

export function PricingGuideCard() {
  const { pct, planName, role } = useTakeRate();
  if (pct == null) return null;
  const word = role === "printer" ? "bid" : "quote";
  const grossFor = (net) => Math.ceil(net / (1 - pct / 100));
  const ex = grossFor(10000);
  const exFee = Math.round(ex * pct) / 100;
  return (
    <div data-testid="pricing-guide-card" style={{ display: "flex", gap: 12, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 16px", margin: "0 0 20px" }}>
      <Calculator size={20} style={{ color: "#1d4ed8", flexShrink: 0 }} />
      <div style={{ fontSize: 13, lineHeight: 1.6, color: "#334155" }}>
        <b style={{ color: "#0F172A" }}>How to price your {word} (Gross Bid · MoR model)</b><br />
        Your platform fee on the <b>{planName}</b> plan is <b data-testid="pricing-guide-rate">{pct}%</b> + 18% GST. Always quote your final price before GST: production cost + your margin + our platform fee. Product GST is added at checkout and paid by the buyer.
        <br />Example: to earn ₹10,000 (before GST), {word} <b data-testid="pricing-guide-example">{inr(ex)}</b>. You invoice us {inr(ex)} + product GST. At payout we deduct the platform fee {inr(exFee)} + {inr(Math.round(exFee * 18) / 100)} GST @18% (billed on a separate tax invoice, so you can claim it as ITC in GSTR-3B) and 1% TDS u/s 194-O on {inr(ex)} (shown in your Form 26AS).
        <br /><span style={{ fontSize: 12, color: "#64748b" }}>Net payout = Gross {word} + product GST − platform fee − 18% GST on the fee − 1% TDS. Upgrade your plan to lower the fee.</span>
      </div>
    </div>
  );
}
